import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService, Notification } from '../services/notificationService';
import { useToast } from '../context/ToastContext';
import styles from '../styles/modules/AlertModal.module.css';

interface AlertModalProps {
  onNotificationRead?: () => void;
}

const AlertModal: React.FC<AlertModalProps> = ({ onNotificationRead }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [alerts, setAlerts] = useState<Notification[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const playAlertSound = useCallback(() => {
    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Alert sound: high pitch beep
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  }, []);

  const fetchAlerts = useCallback(async () => {
    // Don't fetch if we're in the middle of acknowledging
    if (isAcknowledging) {
      console.log('⏸️ Skipping fetch - acknowledging in progress');
      return;
    }
    
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications();
      console.log('📥 Fetched all notifications:', response.notifications.length);

      // Show ALL alerts with their read status for debugging
      const allAlerts = response.notifications.filter(
        (notif: Notification) => notif.category === 'alert'
      );
      console.log('📊 ALL alerts (read + unread):', allAlerts.map(a => ({
        id: a._id.substring(0, 8), 
        title: a.title,
        isRead: a.isRead,
        priority: a.priority
      })));

      // Filter for UNREAD alerts only
      const alertList = response.notifications.filter(
        (notif: Notification) => notif.category === 'alert' && !notif.isRead
      );
      
      console.log('🚨 Unread alert notifications found:', alertList.length);

      setAlerts(alertList);
      if (alertList.length > 0 && !hasPlayed) {
        setShowModal(true);
        playAlertSound();
        setHasPlayed(true);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [hasPlayed, playAlertSound, isAcknowledging]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleNext = () => {
    if (currentIndex < alerts.length - 1) {
      setCurrentIndex(currentIndex + 1);
      playAlertSound();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      playAlertSound();
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleAcknowledge = async () => {
    const currentAlert = alerts[currentIndex];
    console.log('🔔 Acknowledging alert:', currentAlert._id, currentAlert.title);
    
    setIsAcknowledging(true);
    try {
      const response = await notificationService.markAsRead(currentAlert._id);
      console.log('✅ Alert marked as read successfully:', response);
      console.log('📝 Response details - notificationRead.isRead:', response.notificationRead?.isRead);
      
      // Remove acknowledged alert from local state immediately
      const updated = alerts.filter((a) => a._id !== currentAlert._id);
      setAlerts(updated);
      setIsAcknowledging(false);

      // Close modal or move to next
      if (updated.length === 0) {
        handleClose();
        // Notify parent after modal closes
        if (onNotificationRead) {
          setTimeout(() => onNotificationRead(), 100);
        }
      } else if (currentIndex >= updated.length) {
        setCurrentIndex(updated.length - 1);
      }
    } catch (error) {
      console.error('❌ Error acknowledging alert:', error);
      showToast({
        message: t('messages.errorOccurred'),
        type: 'error'
      });
      setIsAcknowledging(false);
    }
  };

  if (!showModal || alerts.length === 0 || loading) {
    return null;
  }

  const currentAlert = alerts[currentIndex];
  const priorityClass = currentAlert.priority === 'urgent' ? styles.alertPriorityUrgent : '';

  return (
    <div className={styles.alertOverlay} onClick={handleClose}>
      <div className={`${styles.alertModal} ${priorityClass}`} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          className={styles.alertClose}
          onClick={handleClose}
          title="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Content */}
        <div className={styles.alertContent}>
          <div className={styles.alertDate}>
            {new Date(currentAlert.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
          <h2 className={styles.alertTitle}>{currentAlert.title}</h2>
          <div className={styles.alertBody}>{currentAlert.content}</div>
        </div>

        {/* Navigation and Actions */}
        <div className={styles.alertFooter}>
          {alerts.length > 1 && (
            <>
              <div className={styles.alertCounter}>
                {currentIndex + 1} of {alerts.length}
              </div>
              <div className={styles.alertNavigation}>
                <button
                  className={styles.btnNav}
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  title="Previous alert"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  className={styles.btnNav}
                  onClick={handleNext}
                  disabled={currentIndex === alerts.length - 1}
                  title="Next alert"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            </>
          )}
          <button className={styles.btnAlertAcknowledge} onClick={handleAcknowledge}>
            {currentIndex === alerts.length - 1 ? 'Acknowledge & Close' : 'Acknowledge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
